/**
 * Projects list side preview - description + todo lists (with sample todos) + recent activity.
 * Cache-first: never auto-fetches on select when local cache is missing (Load preview CTA).
 * Load-request intent is owned by the parent so remounts do not lose in-flight loads.
 */

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { Check, ExternalLink, FolderOpen, ListTodo, Loader2, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PreviewPaneHeader } from '@/components/features/preview-pane-header';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  isTodoListsFetchInflight,
  subscribeTodoListsInflight,
  useBasecampProjectActivity,
  useBasecampTodoLists,
} from '@/hooks/use-basecamp';
import {
  formatRelativeSyncLabel,
  recordProjectPreviewLoaded,
  rememberProjectsPanelSelection,
  resolveProjectCacheDepth,
} from '@/lib/basecamp-project-relevance';
import {
  countTodoListWithSections,
  sampleOpenTodosFromList,
} from '@/lib/basecamp-todolist-counts';
import { isModClick } from '@/lib/open-intent';
import { testIds } from '@/lib/test-ids';
import { cn } from '@/lib/utils';
import type { BasecampProject } from '@/types/basecamp';

const MAX_LISTS = 4;
const MAX_TODOS_PER_LIST = 4;
const MAX_ACTIVITY = 5;

const PREVIEW_SECTION =
  'text-xs font-semibold uppercase tracking-wider text-muted-foreground';
const PREVIEW_META = 'text-xs text-muted-foreground';

function projectWebUrl(project: BasecampProject): string | null {
  const raw = project.app_url || project.url;
  if (!raw) return null;
  return raw.replace('basecampapi.com', 'basecamp.com').replace(/\.json$/, '');
}

function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function openTodoList(
  listId: string,
  listTitle: string,
  projectId: string,
  projectName: string,
  organizationId?: string,
  openInNewTab?: boolean,
) {
  rememberProjectsPanelSelection(projectId, organizationId);
  window.dispatchEvent(
    new CustomEvent('integration-view-change', {
      detail: {
        tabId: `basecamp-todo-list-${listId}`,
        title: listTitle,
        type: 'integration-basecamp-todo-list',
        integrationData: {
          integrationId: 'basecamp',
          projectId,
          listId,
          projectName,
          listName: listTitle,
          ...(organizationId ? { organizationId } : {}),
        },
        ...(openInNewTab ? { openInNewTab: true } : {}),
      },
    }),
  );
}

function openTodo(
  todoId: string,
  title: string,
  projectId: string,
  projectName: string,
  organizationId?: string,
  openInNewTab?: boolean,
) {
  rememberProjectsPanelSelection(projectId, organizationId);
  window.dispatchEvent(
    new CustomEvent('integration-view-change', {
      detail: {
        tabId: `basecamp-todo-${todoId}`,
        title,
        type: 'integration-basecamp-todo',
        integrationData: {
          integrationId: 'basecamp',
          projectId,
          todoId,
          projectName,
          ...(organizationId ? { organizationId } : {}),
        },
        ...(openInNewTab ? { openInNewTab: true } : {}),
      },
    }),
  );
}

function PreviewBodySkeleton() {
  return (
    <div className="mt-5 space-y-5" aria-busy="true" aria-label="Loading preview">
      <div className="space-y-2">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-[80%]" />
        <Skeleton className="h-3 w-[65%]" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-[85%]" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-[90%]" />
      </div>
    </div>
  );
}

export interface BasecampProjectPreviewPaneProps {
  project: BasecampProject;
  organizationId?: string;
  onOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Parent-owned: user asked to load (or Sync) this project preview. Survives remounts. */
  loadRequested?: boolean;
  /** Parent-owned: fetch kicked off for this project and not finished yet. */
  isFetching?: boolean;
  onRequestLoad?: () => void;
  /** Fired when preview content finishes loading so the list can refresh sync labels. */
  onPreviewLoaded?: (projectId: string, loadedAtIso: string) => void;
  onClose: () => void;
}

export function BasecampProjectPreviewPane({
  project,
  organizationId,
  onOpen,
  loadRequested = false,
  isFetching = false,
  onRequestLoad,
  onPreviewLoaded,
  onClose,
}: BasecampProjectPreviewPaneProps) {
  const projectId = project.id?.toString() ?? null;
  const depth = resolveProjectCacheDepth({
    cacheDepth: project.cacheDepth,
    listsFresh: project.listsFresh,
    listsCachedAt: project.listsCachedAt,
    lastSync: project.lastSync,
  });
  // Ok = fresh blob; Partial = expired blob still in Postgres - paint without Load (Rule 5).
  // None = never cached - Load CTA only.
  const orgHasListsBlob = depth === 'ok' || depth === 'partial';
  const inflight = useSyncExternalStore(
    subscribeTodoListsInflight,
    () => isTodoListsFetchInflight(projectId),
    () => false,
  );
  const shouldFetch = orgHasListsBlob || loadRequested || isFetching || inflight;

  const {
    todoLists,
    isLoading: listsLoading,
    error: listsError,
    cached: listsCached,
    cachedAt: listsCachedAt,
    refetch: refetchLists,
  } = useBasecampTodoLists(projectId, {
    enabled: shouldFetch,
    autoRefresh: false,
  });
  const {
    activities,
    isLoading: activityLoading,
    cached: activityCached,
    cachedAt: activityCachedAt,
  } = useBasecampProjectActivity(projectId, 7, {
    enabled: shouldFetch,
    autoRefresh: false,
  });

  const webUrl = projectWebUrl(project);
  const topLists = (todoLists || [])
    .filter((l) => !l.is_nested)
    .slice(0, MAX_LISTS);

  const hasPreviewBody = topLists.length > 0 || Boolean(listsCachedAt);
  // Intent + module inflight: show loading until body/error exists (survives remount).
  const isInitialLoading =
    Boolean(projectId) && shouldFetch && !listsError && !hasPreviewBody;

  const syncIso =
    // Prefer org lists blob age (not activity fetch time) for cache label
    project.listsCachedAt ||
    listsCachedAt ||
    activityCachedAt ||
    project.lastSync ||
    null;
  const syncLabel = isInitialLoading
    ? 'Loading...'
    : !shouldFetch
      ? 'Not loaded'
      : formatRelativeSyncLabel(syncIso);
  const syncSource =
    isInitialLoading
      ? null
      : !shouldFetch
        ? null
        : depth === 'partial'
          ? 'stale'
          : depth === 'ok' || listsCached || activityCached
            ? 'cached'
            : syncIso
              ? 'loaded'
              : null;

  const onPreviewLoadedRef = useRef(onPreviewLoaded);
  onPreviewLoadedRef.current = onPreviewLoaded;
  const recordedForIdRef = useRef<string | null>(null);

  useEffect(() => {
    recordedForIdRef.current = null;
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !shouldFetch || isInitialLoading || listsError) return;
    if (listsLoading) return;
    if (recordedForIdRef.current === projectId) return;

    const iso =
      listsCachedAt || activityCachedAt || new Date().toISOString();
    recordedForIdRef.current = projectId;
    recordProjectPreviewLoaded(projectId);
    onPreviewLoadedRef.current?.(projectId, iso);
  }, [
    projectId,
    shouldFetch,
    isInitialLoading,
    listsError,
    listsLoading,
    listsCachedAt,
    activityCachedAt,
  ]);

  const syncBadge = !shouldFetch
    ? null
    : syncSource === 'stale'
      ? (
          <span className="inline-flex items-center rounded-full border border-amber-700/20 bg-amber-700/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-500">
            Stale
          </span>
        )
      : syncSource === 'cached'
        ? (
            <span className="inline-flex items-center rounded-full border border-green-600/20 bg-green-600/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-green-600 dark:border-green-500/20 dark:bg-green-500/10 dark:text-green-500">
              Cached
            </span>
          )
        : null;

  return (
    <div
      className="flex flex-1 flex-col overflow-hidden"
      data-testid={testIds.basecamp.projectsPreview}
    >
      <PreviewPaneHeader
        row
        testId={testIds.basecamp.projectsPreviewHeader}
        icon={<FolderOpen className="h-4 w-4 text-muted-foreground" />}
        chips={syncBadge}
        onOpen={onOpen}
        onClose={onClose}
        openTitle="Open project"
        dropdownContent={
          webUrl ? (
            <>
              <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Actions
              </DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <a href={webUrl} target="_blank" rel="noopener noreferrer" className="flex items-center">
                  <ExternalLink className="mr-2 h-3.5 w-3.5" />
                  Open in Basecamp
                </a>
              </DropdownMenuItem>
              {onRequestLoad && !shouldFetch ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onRequestLoad()}>
                    <Loader2 className="mr-2 h-3.5 w-3.5" />
                    Load preview
                  </DropdownMenuItem>
                </>
              ) : null}
            </>
          ) : onRequestLoad && !shouldFetch ? (
            <>
              <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Actions
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onRequestLoad()}>
                <Loader2 className="mr-2 h-3.5 w-3.5" />
                Load preview
              </DropdownMenuItem>
            </>
          ) : undefined
        }
      />
      <div className="flex flex-1 flex-col overflow-auto p-4">
        <h3 className="text-lg font-semibold">{project.name}</h3>
        <p className={cn('text-xs text-muted-foreground')}>
          {!shouldFetch
            ? 'Preview not loaded'
            : `${
                syncSource === 'stale'
                  ? 'Stale'
                  : syncSource === 'cached'
                    ? 'Cached'
                    : syncSource === 'loaded'
                      ? 'Loaded'
                      : 'Last sync'
              }: ${syncLabel}`}
        </p>

        {!shouldFetch ? (
          <div className="mt-5 flex flex-1 flex-col">
            {project.description ? (
              <p className="line-clamp-4 text-[13px] leading-[1.55] text-muted-foreground">
                {project.description}
              </p>
            ) : null}
            <div className="mt-5 rounded-md border border-border/80 bg-muted/20 px-3 py-4">
              <p className="text-sm font-medium">Preview not loaded</p>
              <p className="mt-1 text-[13px] leading-[1.55] text-muted-foreground">
                Loads todo lists and recent activity from Basecamp for this project.
                Large projects can take a while.
              </p>
              <Button
                size="sm"
                className="mt-3"
                onClick={() => onRequestLoad?.()}
              >
                Load preview
              </Button>
            </div>
          </div>
        ) : isInitialLoading ? (
          <div className="mt-5 flex flex-1 flex-col">
            {project.description ? (
              <p className="mb-4 line-clamp-4 text-[13px] leading-[1.55] text-muted-foreground">
                {project.description}
              </p>
            ) : null}
            <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {orgHasListsBlob
                ? 'Loading workspace cache...'
                : 'Loading preview from Basecamp...'}
            </div>
            <PreviewBodySkeleton />
          </div>
        ) : (
          <div className="flex flex-1 flex-col">
            {project.description ? (
              <p className="mt-3 line-clamp-4 text-[13px] leading-[1.55] text-foreground/80">
                {project.description}
              </p>
            ) : null}

            <section className="mt-5">
            <div className="mb-2 flex items-center justify-between">
              <h3 className={PREVIEW_SECTION}>Todo lists</h3>
              {topLists.length > 0 ? (
                <span className={PREVIEW_META}>
                  {topLists.length}
                  {(todoLists?.filter((l) => !l.is_nested).length ?? 0) >
                  MAX_LISTS
                    ? '+'
                    : ''}
                </span>
              ) : null}
            </div>
            {listsLoading && topLists.length === 0 ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-[85%]" />
              </div>
            ) : listsError ? (
              <div className="space-y-2">
                <p className={PREVIEW_META}>Could not load lists.</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onRequestLoad?.();
                    void refetchLists(true);
                  }}
                >
                  {listsLoading ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  Retry sync
                </Button>
              </div>
            ) : topLists.length === 0 ? (
              <p className={PREVIEW_META}>No todo lists yet.</p>
            ) : (
              <ul className="space-y-3">
                {topLists.map((list) => {
                  const counts = countTodoListWithSections(list, todoLists || []);
                  const sample = sampleOpenTodosFromList(
                    list,
                    todoLists || [],
                    MAX_TODOS_PER_LIST,
                  );
                  const more = Math.max(0, counts.open - sample.length);
                  return (
                    <li key={list.id}>
                      <button
                        type="button"
                        className="flex w-full items-center gap-1.5 rounded-md px-1 py-1 text-left text-sm font-medium hover:bg-accent/40"
                        title="Open list"
                        onClick={(e) =>
                          openTodoList(
                            list.id,
                            list.title,
                            project.id.toString(),
                            project.name,
                            organizationId,
                            isModClick(e),
                          )
                        }
                      >
                        <ListTodo className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate text-foreground">
                          {list.title}
                        </span>
                        <span className={cn('shrink-0', PREVIEW_META)}>
                          {counts.open} open
                        </span>
                      </button>
                      {sample.length > 0 ? (
                        <ul className="mt-1 space-y-0.5 border-l border-border/60 ml-2 pl-2">
                          {sample.map((t) => (
                            <li key={t.id}>
                              <button
                                type="button"
                                className="flex w-full items-center gap-1.5 rounded px-1 py-1 text-left text-sm text-foreground/90 hover:bg-accent/40 hover:text-foreground"
                                title="Open todo"
                                onClick={(e) =>
                                  openTodo(
                                    t.id.toString(),
                                    t.title || 'Todo',
                                    project.id.toString(),
                                    project.name,
                                    organizationId,
                                    isModClick(e),
                                  )
                                }
                              >
                                {t.completed ? (
                                  <Check className="h-3.5 w-3.5 shrink-0" />
                                ) : (
                                  <Square className="h-3.5 w-3.5 shrink-0" />
                                )}
                                <span className="min-w-0 flex-1 truncate">
                                  {t.title || 'Untitled'}
                                </span>
                              </button>
                            </li>
                          ))}
                          {more > 0 ? (
                            <li className={cn('px-1 py-0.5', PREVIEW_META)}>
                              +{more} more
                            </li>
                          ) : null}
                        </ul>
                      ) : (
                        <p className={cn('ml-2 mt-0.5 pl-2', PREVIEW_META)}>
                          No open todos
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="mt-5">
            <h3 className={cn('mb-2', PREVIEW_SECTION)}>Recent activity</h3>
            {activityLoading && (!activities || activities.length === 0) ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-[85%]" />
              </div>
            ) : !activities || activities.length === 0 ? (
              <p className={PREVIEW_META}>No recent activity (7 days).</p>
            ) : (
              <ul className="space-y-2">
                {activities.slice(0, MAX_ACTIVITY).map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      className="w-full rounded-md px-1 py-1 text-left hover:bg-accent/40"
                      onClick={(e) => {
                        if (a.type === 'todo') {
                          const todoId =
                            a.recording_id ||
                            a.url?.match(/\/todos\/(\d+)/)?.[1] ||
                            null;
                          if (todoId) {
                            openTodo(
                              todoId.toString(),
                              a.title,
                              project.id.toString(),
                              project.name,
                              organizationId,
                              isModClick(e),
                            );
                            return;
                          }
                        }
                        if (a.url) window.open(a.url, '_blank');
                      }}
                    >
                      <p className="truncate text-sm font-medium leading-snug">
                        {a.title}
                      </p>
                      <p className={PREVIEW_META}>
                        {a.creator?.name ? `${a.creator.name} · ` : ''}
                        {getTimeAgo(a.created_at)}
                        {a.type ? ` · ${a.type}` : ''}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
        )}
      </div>
    </div>
  );
}
